import React, { useEffect, useState } from 'react'
import { getOracleColor } from './getOracleComputedStyle'

export interface OracleLoadingSpinnerProps {
  size?: number
  circleColor?: string
  progressColor?: string
  weight?: number
  gap?: number
  duration?: number
  className?: string
}

interface Point {
  x: number
  y: number
}

export const OracleLoadingSpinner: React.FC<OracleLoadingSpinnerProps> = ({
  size = 40,
  circleColor,
  progressColor,
  weight = 4,
  gap = 0.5,
  duration = 5000,
  className = '',
}) => {
  const [progressAngle, setProgressAngle] = useState(0)

  // Use CSS variables if not provided as props
  const finalCircleColor = circleColor || getOracleColor('--oracle-accent-light', 'oracle-p20')
  const finalProgressColor = progressColor || getOracleColor('--oracle-p60', 'oracle-p60')

  // Calculate SVG properties
  const center = size / 2
  const radius = (size - weight) / 2

  // Helper function: Convert polar coordinates to cartesian
  const polarToCartesian = (
    centerX: number,
    centerY: number,
    rad: number,
    angleInRadians: number,
  ): Point => {
    return {
      x: centerX + (rad * Math.cos(angleInRadians - Math.PI / 2)), // -π/2 to start at top
      y: centerY + (rad * Math.sin(angleInRadians - Math.PI / 2)),
    }
  }

  // Helper function: Generate SVG arc path
  const describeArc = (
    x: number,
    y: number,
    rad: number,
    startAngle: number,
    endAngle: number,
  ): string => {
    // Normalize angles
    const normalizedStart = startAngle % (2 * Math.PI)
    const normalizedEnd = endAngle % (2 * Math.PI)

    // If the arc length is very small, return empty path
    const arcLength = normalizedEnd >= normalizedStart
      ? normalizedEnd - normalizedStart
      : (2 * Math.PI) - normalizedStart + normalizedEnd

    if (arcLength < 0.01) return '' // Too small to render

    const start = polarToCartesian(x, y, rad, normalizedStart)
    const end = polarToCartesian(x, y, rad, normalizedEnd)

    const largeArcFlag = arcLength <= Math.PI ? '0' : '1'

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y,
    ].join(' ')
  }

  // Calculate current progress angle (ø) from 0 to 2π
  const phi = progressAngle * 2 * Math.PI

  // Calculate angular extents based on your whiteboard math
  // Progress arc: from [g/2] to [ø]
  const progStartAngle = gap / 2
  const progEndAngle = phi
  const progArcLength = phi - gap / 2

  // Circle arc: from [ø + g] to [2π - g/2]
  const circStartAngle = phi + gap
  const circEndAngle = 2 * Math.PI - gap / 2
  const circArcLength = 2 * Math.PI - phi - ((3 * gap) / 2)

  // Generate SVG paths
  const progressPath = progArcLength > 0 ? describeArc(center, center, radius, progStartAngle, progEndAngle) : ''
  const circlePath = circArcLength > 0 ? describeArc(center, center, radius, circStartAngle, circEndAngle) : ''

  // Animation effect - continuous loop
  useEffect(() => {
    let animationId: number

    const animate = () => {
      const startTime = Date.now()

      const updateProgress = () => {
        const elapsed = Date.now() - startTime
        const progress = (elapsed % duration) / duration // Loop continuously

        setProgressAngle(progress)
        animationId = requestAnimationFrame(updateProgress)
      }

      updateProgress()
    }

    animate()

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId)
      }
    }
  }, [duration])

  return (
    <div
      className={`oracle-loading-spinner ${className}`}
      style={{
        width: size,
        height: size,
      }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="oracle-loading-spinner__svg"
      >
        {/* Background circle arc - shrinks over time */}
        {circlePath && (
          <path
            d={circlePath}
            fill="none"
            stroke={finalCircleColor}
            strokeWidth={weight}
            strokeLinecap="round"
            className="oracle-loading-spinner__background"
          />
        )}

        {/* Progress arc - grows over time */}
        {progressPath && (
          <path
            d={progressPath}
            fill="none"
            stroke={finalProgressColor}
            strokeWidth={weight}
            strokeLinecap="round"
            className="oracle-loading-spinner__progress"
          />
        )}
      </svg>
    </div>
  )
}
